import { executeOnce, type ReadonlyDate, type ReadonlyRecord } from '../../utils'

import type { Capability } from '..'
import type { Loadable } from '../Loadable'

export type AuthenticationStatus = ReadonlyRecord<'isMfaEnabled' | 'isMfaRequired', boolean>

export type AuthenticationInformation = Readonly<{
    /**
     * The source of the token
     */
    username: string

    /**
     * The auth token
     */
    token: string

    /**
     * When the token will expire
     */
    expiresAt: ReadonlyDate

    /**
     * If the authentication is soon to expire, this will be set to true
     */
    expiresSoon: boolean

    /**
     * The capabilities the user has on the application
     */
    capabilities: readonly Capability[]

    /**
     * Will be non-null if the current authentication is found to no longer be valid
     */
    obsolete: Loadable<AuthenticationStatus>
}>

const createSelector = <K extends keyof AuthenticationInformation>(k: K): (s: Pick<AuthenticationInformation, K>) => AuthenticationInformation[K] => (a) => a[k]

export const selectAuthenticationToken = createSelector('token')
export const selectAuthenticationUsername = createSelector('username')
export const selectAuthenticationExpiresAt = createSelector('expiresAt')
export const selectAuthenticationExpiresSoon = createSelector('expiresSoon')
export const selectAuthenticationCapabilities = createSelector('capabilities')
export const selectAuthenticationObsolescence = createSelector('obsolete')

export const createAreAllCapabilitiesIncludedChecker = (
    ...[s]: Parameters<typeof selectAuthenticationCapabilities>
): (requiredCapabilities: readonly Capability[]) => boolean => {
    const getIndexedCapabilities = executeOnce(() => new Set(selectAuthenticationCapabilities(s)))
    return (requiredCapabilities) => requiredCapabilities.every((p) => getIndexedCapabilities().has(p))
}
